import { GCS_BUCKET_URI } from '@/exports';
import extractBaseUrl from '@/utils/extractBaseUrl';
import getFileExtension from '@/utils/getFileExtension';
import getImageUrl from '@/utils/getImageUrl';
import getMmsAltText from '@/utils/getMmsAltText';
import { Avatar, Box, Skeleton } from '@mui/material';
import moment from 'moment';
import { usePathname, useRouter } from 'next/navigation';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import { UserAuth } from '@/app/context/AuthContext';
import BookmarkStar from '../@generalComponents/BookmarkStar';
import SignUpModal from '../@generalComponents/SignUpModal';
import { MessageCardSkeleton } from './MessageCardSkeleton';
import {
	bookmarkActions,
	bookmarkSelectors,
} from '@/redux/reducers/bookmark.reducer';
import getVigLinkUrl from '@/utils/getVigLinkUrl';
import getBrandDisplayName from '@/utils/getBrandDisplayName';
import {
	getEffectiveTags,
	FILTER_TAG_LABELS,
	ALLOWED_MANUAL_TAGS,
	AUTO_TAG_RCS,
	AUTO_TAG_COUPON,
} from '@/utils/messageFilters';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards } from 'swiper/modules';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FaExternalLinkAlt } from 'react-icons/fa';

import 'swiper/css';
import 'swiper/css/effect-cards';

const categoriesToExclude = [
	'6519604594c1b61a4d62db72',
	'6519604594c1b61a4d62db6a',
	'6519604594c1b61a4d62db74',
	'6519604594c1b61a4d62db70',
];

const LINK_REGEX =
	/\b(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+[^\s]*/g;

// Editorial (manual, admin-set) tags are the point of this label row, so they
// come first; the auto-derived RCS/Coupon tags follow. Any future/unknown tag
// slug (not in this list) is appended at the end rather than dropped, and
// falls back to a `#slug` label the same way TagFilter does.
const CARD_TAG_ORDER = [...ALLOWED_MANUAL_TAGS, AUTO_TAG_RCS, AUTO_TAG_COUPON];

// Renders the raw message body with each link inlined at its original
// position (instead of extracted and appended below the text), so text
// and link line spacing matches the source message.
const renderMessageBody = (body, brandId, router) => {
	if (!body) return null;

	const nodes = [];
	const regex = new RegExp(LINK_REGEX.source, 'g');
	let lastIndex = 0;
	let match;
	let key = 0;

	while ((match = regex.exec(body)) !== null) {
		if (match.index > lastIndex) {
			nodes.push(body.slice(lastIndex, match.index));
		}

		const rawUrl = match[0];
		const affiliateUrl = getVigLinkUrl(rawUrl);

		nodes.push(
			<a
				key={`link-${key++}`}
				href={affiliateUrl || '#'}
				target='_blank'
				data-viglink='true'
				style={{ color: '#0000EE' }}
				onClick={(e) => {
					if (!affiliateUrl) {
						e.preventDefault();
						router.push(`/brand/${brandId}`);
					}
				}}
			>
				{extractBaseUrl(rawUrl)}
			</a>
		);

		lastIndex = regex.lastIndex;
	}

	if (lastIndex < body.length) {
		nodes.push(body.slice(lastIndex));
	}

	return nodes;
};

const MessageCard = ({ message, variant = 'normal' }) => {
	const pathname = usePathname();
	const router = useRouter();
	const { user, userLoading, googleSignIn, logOut } = UserAuth();
	const [isLoaded, setIsLoaded] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false); // New state for modal
	const [pendingBookmark, setPendingBookmark] = useState(false); // To track bookmark click after login
	const [imageLoaded, setImageLoaded] = useState(false);
	const swiperRef = useRef(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const dispatch = useDispatch();
	const isBookmarksPage = pathname.includes('/bookmarks');

	const bookmarkedMessages = useSelector(
		bookmarkSelectors.getBookmarkedMessages
	);
	const isBookmarked = bookmarkedMessages?.some(
		(item) => item.messageId === message.messageId
	);

	const handleCardClick = () => {
		const swiper = swiperRef.current;
		if (!swiper) return;

		if (swiper.activeIndex === message?.botMessages?.length - 1) {
			swiper.slideTo(0); // last → back to first
		} else {
			swiper.slideNext(); // normal advance
		}
	};

	const toggleBookmark = async () => {
		if (!user) {
			setPendingBookmark(true);
			setIsModalOpen(true);
			return;
		}

		// Tier not known yet: ignore the tap until the viewer is settled.
		if (userLoading) return;

		dispatch(
			bookmarkActions.toggleBookmark(user.uid, message.messageId, message)
		);
	};

	const handleSignUp = async () => {
		try {
			await googleSignIn();
			setIsModalOpen(false);
			if (pendingBookmark) {
				// After login, complete the bookmark action
				// dispatch({ type: 'ADD_BOOKMARK', payload: message });
				setPendingBookmark(false);
			}
		} catch (error) {
			console.error('Error signing in:', error);
		}
	};

	const closeSignUpModal = () => {
		setIsModalOpen(false);
	};

	useEffect(() => {
		const timer = setTimeout(() => setIsLoaded(true), 1000);
		return () => clearTimeout(timer);
	}, []);

	const onAvatarClick = () => {
		if (pathname.includes('brand')) {
			if (brandAffiliateUrl) {
				window?.open(brandAffiliateUrl, '_blank');
			}
			return;
		}

		if (message?.brand?.brandId) {
			router.push(`/brand/${message?.brand?.brandId}`);
		}
	};

	const isBrandsPage = pathname.includes('brands');
	const brandName = getBrandDisplayName(message?.brand);
	const brandAffiliateUrl = getVigLinkUrl(message?.brand?.websiteLink);

	// Computed here (rather than relying on the parent having called
	// `withEffectiveTags`) because MessageCard is shared across the home,
	// brand, category, search and bookmarks pages, and not all of them
	// annotate messages with effective tags before passing them in.
	const effectiveTags = getEffectiveTags(message);
	const displayTags = [
		...CARD_TAG_ORDER.filter((tag) => effectiveTags.includes(tag)),
		...effectiveTags.filter((tag) => !CARD_TAG_ORDER.includes(tag)),
	];

	// Show skeleton while loading
	if (!isLoaded || !message) {
		return <MessageCardSkeleton variant={variant} />;
	}

	const avatarContent = (
		<Avatar
			alt={`${message?.brand?.name} logo`}
			sx={{
				width: '60px',
				height: '60px',
				cursor: 'pointer',
				objectFit: 'contain',
			}}
			src={
				message?.brand?.iconObjectName
					? `${GCS_BUCKET_URI}/brand-icons/${message?.brand?.iconObjectName}`
					: '/images/avatar-icon.svg'
			}
			imgProps={{ loading: 'lazy' }}
			onClick={isBrandsPage ? undefined : onAvatarClick}
			data-viglink='true'
		/>
	);

	const imageContent = (
		<div className='mb-2'>
			{!imageLoaded && (
				<Skeleton
					variant='rectangular'
					width='85%'
					height={200}
					sx={{ borderRadius: '10px' }}
				/>
			)}
			<Image
				alt={getMmsAltText(message?.body, message?.brand?.name)}
				className={`rounded-[10px] !w-[85%] ${
					!imageLoaded ? 'opacity-0 absolute' : 'opacity-100'
				}`}
				src={getImageUrl(message?.mediaObjectName)}
				width={500}
				height={300}
				layout='responsive'
				loading='lazy'
				unoptimized
				sizes='(max-width: 768px) 100vw, 500px'
				srcSet={`
${getImageUrl(message?.mediaObjectName)}?w=500 500w,
${getImageUrl(message?.mediaObjectName)}?w=1000 1000w,
${getImageUrl(message?.mediaObjectName)}?w=1500 1500w
`}
				onLoad={() => setImageLoaded(true)}
			/>
		</div>
	);

	return (
		<div
			className={`flex md:block ${
				variant == 'normal'
					? 'justify-center'
					: 'w-full max-w-[270px] min-w-[270px]'
			} mb-3`}
		>
			<div className='relative w-full'>
				<div className='w-full mx-auto rounded-[24px] border-2 border-gray-200 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] min-h-[550px] max-w-[268px]'>
					<div className='border-b-2 border-gray-100 pt-[13px] pb-[13px]'>
						<div className='flex flex-col items-center justify-center'>
							{isBrandsPage && message?.brand?.websiteLink ? (
								<Link
									href={brandAffiliateUrl}
									passHref={true}
									target='_blank'
									data-viglink='true'
								>
									{avatarContent}
								</Link>
							) : (
								<span>{avatarContent}</span>
							)}

							<p className='text-center text-[#020314] text-base font-medium'>
								{brandName}
							</p>
						</div>
					</div>
					{/* HANDLE MMS/SMS MESSAGES */}
					{!message?.botMessages && (
						<div className='p-[19px] h-[436px] overflow-y-auto cursor-pointer'>
							{message?.mediaObjectName &&
								getFileExtension(message?.mediaObjectName) !== 'vcf' && (
									<Link
										href={brandAffiliateUrl}
										passHref={true}
										target='_blank'
										data-viglink='true'
									>
										{imageContent}
									</Link>
								)}

							<div
								className='rounded-[10px] bg-gray-100 text-[#020314] text-sm font-normal p-1 md:p-2 break-words'
								style={{ whiteSpace: 'pre-line' }}
							>
								<div className='text-sm md:text-base'>
									{renderMessageBody(message?.body, message?.brand?.brandId, router)}
								</div>
							</div>
						</div>
					)}
					{/* HANDLE RCS BOT MESSAGES */}
					{message?.botMessages && (
						<div
							className='p-[19px] h-[436px] overflow-y-auto'
							style={{ overflowX: 'hidden' }}
						>
							<Swiper
								onSwiper={(swiper) => {
									swiperRef.current = swiper;
									setActiveIndex(swiper.activeIndex); // ← sync on init
								}}
								onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
								effect='cards'
								grabCursor
								modules={[EffectCards]}
								autoHeight
								cardsEffect={{
									slideShadows: false,
									perSlideOffset: 8,
									perSlideRotate: 2,
								}}
								className='max-w-[210px] min-w-[210px]'
							>
								{message?.botMessages?.map((botMsg, i) => {
									// Some bot messages carry no action URL — fall back to the
									// brand's site so the CTA still goes somewhere real
									const ctaUrl =
										getVigLinkUrl(botMsg?.action?.url) || brandAffiliateUrl;

									return (
									<SwiperSlide
										key={i}
										onClick={handleCardClick}
										className='rounded-[24px] overflow-hidden bg-white shadow-md cursor-pointer select-none'
									>
										<div className='bg-gray-200'>
											{/* Image */}
											{botMsg?.mediaUrl && (
												<div className='w-full'>
													<img
														src={botMsg?.mediaUrl}
														alt={botMsg?.body?.title || 'Collection preview'}
														// Dead/expired mediaUrls otherwise render as a broken
														// image box with alt text taking up the slide
														onError={(e) => {
															e.currentTarget.parentElement.style.display = 'none';
														}}
														className='w-full h-auto object-cover object-top'
													/>
												</div>
											)}
											{/* Height follows the content (Swiper autoHeight) — full
											    text is shown and the card body scrolls when it
											    overflows, matching the live site */}
											<div className='mt-1 px-4 pt-3 pb-4'>
												{/* title */}
												<div
													className='break-words'
													style={{ fontSize: '15px', fontWeight: 500 }}
												>
													{botMsg?.body?.title}
												</div>
												<div
													className='break-words'
													style={{ fontSize: '14px' }}
												>
													{botMsg?.body?.description}
												</div>
												{/* Button */}
												{ctaUrl && (
													<Link
														href={ctaUrl}
														passHref={true}
														target='_blank'
														data-viglink='true'
														// The slide itself is a click target that advances the
														// carousel — keep the button's click from bubbling into it
														onClick={(e) => e.stopPropagation()}
														className='w-full mt-2 flex items-center bg-gray-100 transition-colors rounded-xl px-4 py-2'
													>
														<FaExternalLinkAlt className='shrink-0' />
														<p
															className='text-base text-gray-900 mb-0 truncate'
															style={{ marginLeft: 10 }}
														>
															{botMsg?.action?.text || 'Shop Now'}
														</p>
													</Link>
												)}
											</div>
										</div>
									</SwiperSlide>
									);
								})}
							</Swiper>
						</div>
					)}
				</div>
				{displayTags.length > 0 && (
					<ul
						aria-label='Tags'
						className='flex flex-wrap justify-center gap-1 list-none w-full max-w-[268px] mx-auto px-3 pt-1.5 m-0'
					>
						{displayTags.map((tag) => (
							<li
								key={tag}
								className='rounded-[6px] border border-[#B1ABAB]/50 bg-gray-50 px-1.5 py-0.5 text-[10px] leading-none font-medium text-gray-500'
							>
								{FILTER_TAG_LABELS[tag] || `#${tag}`}
							</li>
						))}
					</ul>
				)}
				<div className='flex flex-col items-center justify-center w-full max-w-[268px] mx-auto px-2 pt-1 pb-0'>
					<p className='text-[#020314] text-base font-bold mb-0 text-center truncate w-full'>
						{brandName}
					</p>
					<div className='w-full relative flex items-center justify-center px-1'>
						<p className='mt-0 mb-0 text-sm font-normal text-gray-500 whitespace-nowrap'>
							{moment(message?.createdAt).format('ddd, MM/DD/YYYY')}
						</p>
						<Box
							sx={{
								display: 'flex',
								cursor: 'pointer',
								flexShrink: 0,
								position: 'absolute',
								right: 4,
							}}
							onClick={toggleBookmark}
						>
							<BookmarkStar isBookmarked={isBookmarked} />
						</Box>
					</div>
				</div>
			</div>

			{/* SignUp Modal */}
			<SignUpModal
				open={isModalOpen}
				onClose={closeSignUpModal}
				onSignUp={handleSignUp}
				onSignIn={handleSignUp}
				title='Bookmark Your Favorites'
				description='Create a free account to save your favorite texts in one place.'
				ctaLabel='Join for free'
			/>
		</div>
	);
};

export default MessageCard;
